import React, { CSSProperties, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Environment } from "@core/config/environment";
import { useIsMobile } from "@utils/hooks";
import { mobile } from "@ui/styles/utils";
import { Z } from "@ui/styles/zIndex";
import { DeleteIcon, SearchIcon, SlimSarchIcon } from "@ui/components/icons";
import { SimpleTextInput } from "@ui/components/inputs/simpleTextInput";
import { AutocompletionView, AutocompletionViewHandle } from "./autocompletionView";

interface SearchBarProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange" | "onSubmit" | "color"> {
	value: string;
	onChange?: (value: string) => void;
	onSubmit?: (value: string) => void;
	placeholder?: string;
	autoFocus?: boolean;
	absoluteAutocompletionActive?: boolean;
	containerStyle?: CSSProperties;
}

export const SearchBar: React.FC<SearchBarProps> = ({
	value: debouncedValue,
	onChange,
	onSubmit,
	absoluteAutocompletionActive,
	containerStyle,
	...htmlProps
}) => {
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [isFocused, setIsFocused] = useState(false);
	const [inputValue, setInitialInputValue] = useState(debouncedValue ?? "");
	const autocompletionViewRef = useRef<AutocompletionViewHandle>(null);
	const isMobile = useIsMobile();

	const setInputValue = (value: string): void => setInitialInputValue(value);
	useEffect(() => {
		setInputValue(debouncedValue);
	}, [debouncedValue]);

	return (
		<InputContainer onFocus={(e) => htmlProps.onFocus?.(e)} style={containerStyle}>
			<StyledTextInput
				{...htmlProps}
				value={inputValue}
				onSubmit={(value) => {
					if (!autocompletionViewRef.current?.isHovered) {
						onSubmit?.(value);
					}
					setIsFocused(false);
				}}
				onChange={(value) => {
					setIsFocused(true);
					setInputValue(value);
					onChange?.(value);
				}}
				inputRef={inputRef}
				onFocus={() => setIsFocused(true)}
				onBlur={() => setIsFocused(false)}
				icon={
					inputValue ? (
						<StyledDeleteIcon
							onClick={() => {
								setInputValue("");
								onChange?.("");
								onSubmit?.("");
								if (!isMobile) {
									inputRef.current?.focus();
								}
							}}
						/>
					) : Environment.APP_VARIANT === "jaangal" ? (
						<SlimSarchIcon />
					) : (
						<SearchIcon />
					)
				}
			/>
			{absoluteAutocompletionActive && (
				<Relative>
					<AbsoluteAutocompletionView
						ref={autocompletionViewRef}
						inputRef={inputRef}
						inputValue={debouncedValue}
						onSelected={(value) => {
							onChange?.(value);
							onSubmit?.(value);
							setInputValue(value);
							setIsFocused(false);
						}}
						onReset={() => {
							onChange?.("");
							setInputValue("");
							setIsFocused(false);
						}}
						hidden={!isFocused}
					/>
				</Relative>
			)}
		</InputContainer>
	);
};

const InputContainer = styled.div`
	position: relative;
	max-width: 600px;
	@media ${mobile} {
		width: 100%;
	}
`;

const StyledTextInput = styled(SimpleTextInput)`
	.input-wrapper {
		border-radius: ${Environment.APP_VARIANT === "jaangal" ? "10px" : "20px"};

		input {
			padding: 10px 15px;
			width: 100%;
		}
	}
`;

const StyledDeleteIcon = styled(DeleteIcon)`
	cursor: pointer;
`;

const Relative = styled.div`
	position: relative;
`;

const AbsoluteAutocompletionView = styled(AutocompletionView)`
	width: 100%;
	position: absolute;
	left: 0px;
	top: 10px;
	border-radius: 10px;
	overflow: hidden;
	background-color: ${({ theme }) => theme.colors.backgroundPrimary};
	box-shadow: 0 2px 14px 0 rgba(0, 0, 0, 0.11);
	z-index: ${Z.modal};

	> :first-child {
		padding-top: 10px;
	}
	> :last-child {
		padding-bottom: 10px;
	}
`;
